import { CheckCircle2, Link2, Loader2, Unlink } from "lucide-react";
import type { AvailableIntegration } from "@/lib/services";

interface IntegrationRowProps {
  integration: AvailableIntegration;
  busy: boolean;
  onConnect: (slug: string) => void;
  onDisconnect: (slug: string) => void;
}

/** A single integration card: metadata on the left, connect/disconnect action on the right. */
export function IntegrationRow({ integration, busy, onConnect, onDisconnect }: IntegrationRowProps) {
  const isOAuth = integration.credentialType === "oauth";

  return (
    <div className="flex items-start justify-between gap-4 p-4 bg-surface-primary border border-border-primary rounded-xl">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-content-primary">{integration.name}</h3>
          {integration.connected && (
            <span className="inline-flex items-center gap-1 text-xs text-green-500">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Connected
            </span>
          )}
        </div>
        {integration.description && <p className="text-sm text-content-tertiary mt-0.5">{integration.description}</p>}
        {!integration.connected && isOAuth && (
          <p className="text-xs text-content-quaternary mt-1">
            Connecting redirects you to {integration.name} to authorize. Actions run as your account.
          </p>
        )}
        {integration.accountLabel && (
          <p className="text-xs text-content-quaternary mt-1 truncate">{integration.accountLabel}</p>
        )}
      </div>
      <div className="shrink-0">
        <IntegrationActionButton
          integration={integration}
          busy={busy}
          isOAuth={isOAuth}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
        />
      </div>
    </div>
  );
}

interface IntegrationActionButtonProps {
  integration: AvailableIntegration;
  busy: boolean;
  isOAuth: boolean;
  onConnect: (slug: string) => void;
  onDisconnect: (slug: string) => void;
}

function IntegrationActionButton({
  integration,
  busy,
  isOAuth,
  onConnect,
  onDisconnect,
}: IntegrationActionButtonProps) {
  if (integration.connected) {
    return (
      <button
        type="button"
        onClick={() => onDisconnect(integration.slug)}
        disabled={busy}
        className="flex items-center gap-2 px-3 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
        Disconnect
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => isOAuth && onConnect(integration.slug)}
      disabled={busy || !isOAuth}
      title={isOAuth ? undefined : "This integration type is not yet supported in the UI."}
      className="flex items-center gap-2 px-3 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
      Connect
    </button>
  );
}
